'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
// Migration jobs endpoint removed - no longer needed
import { MigrationProgressBar } from './MigrationStatus';

interface MigrationNotificationProps {
  jobId: string;
  entityType: string;
  entityCount: number;
  progress: number;
  onDismiss: (jobId: string) => void;
}

const MigrationNotification: React.FC<MigrationNotificationProps> = ({
  jobId,
  entityType,
  entityCount,
  progress,
  onDismiss
}) => {
  return (
    <Card className="max-w-sm border-l-4 border-l-blue-500 animate-slide-in shadow-lg">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🔄</span>
              <div>
                <p className="font-medium text-foreground">Migration in Progress</p>
                <p className="text-sm text-muted-foreground">
                  {entityType}: {entityCount} items
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <MigrationProgressBar 
                progress={progress}
              />
              <p className="text-xs text-muted-foreground">
                {Math.round(progress)}% complete
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 -mr-2 -mt-2"
            onClick={() => onDismiss(jobId)}
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const MigrationNotifications: React.FC = () => {
  // Migration jobs endpoint was removed as it wasn't implemented in the backend
  // This component is now disabled until migration functionality is properly implemented
  return null;
};

interface PlatformStatusNotificationProps {
  platform: 'voluum' | 'redtrack';
  status: 'online' | 'offline' | 'degraded';
  message?: string;
  onDismiss?: () => void;
}

export const PlatformStatusNotification: React.FC<PlatformStatusNotificationProps> = ({
  platform,
  status,
  message,
  onDismiss
}) => {
  const statusConfig = {
    online: {
      borderColor: 'border-l-green-500',
      bgColor: 'bg-green-50',
      icon: '✅',
      title: 'Platform Online'
    },
    offline: {
      borderColor: 'border-l-red-500',
      bgColor: 'bg-red-50',
      icon: '❌',
      title: 'Platform Offline'
    },
    degraded: {
      borderColor: 'border-l-yellow-500',
      bgColor: 'bg-yellow-50',
      icon: '⚠️',
      title: 'Platform Degraded'
    }
  };

  const config = statusConfig[status];

  return (
    <Card className={cn("max-w-sm border-l-4 animate-slide-in shadow-lg", config.borderColor, config.bgColor)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{config.icon}</span>
            <div>
              <p className="font-medium text-foreground">{config.title}</p>
              <p className="text-sm text-muted-foreground capitalize">
                {platform} {status === 'online' ? 'is back online' : `is ${status}`}
              </p>
              {message && (
                <p className="text-xs text-muted-foreground mt-1">{message}</p>
              )}
            </div>
          </div>
          
          {onDismiss && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -mr-2 -mt-2"
              onClick={onDismiss}
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MigrationNotifications;
